import { Component, inject, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';
import { UserService } from '../services/user.service';
import { AuthService } from '../../auth/services/auth.service';
import { UserProfileResponse, Pet, Address, Vehicle, Dependent } from '../models/user-profile.model';
import { AddPolizaModalComponent } from './add-poliza-modal/add-poliza-modal.component';
import { Product } from '../models/product.model';
import { LoaderService } from '../../../shared/services/loader.service';
import { NotificationService } from '../../../shared/services/notification.service';
import { MatDialog } from '@angular/material/dialog';

@Component({
  selector: 'app-profile',
  imports: [CommonModule, MatIconModule, MatFormFieldModule, MatSelectModule, MatButtonModule],
  templateUrl: './profile.component.html',
  styleUrl: './profile.component.scss'
})
export class ProfileComponent implements OnInit {
  private router = inject(Router);
  private userService = inject(UserService);
  private authService = inject(AuthService);


  // Datos del perfil
  pets: Pet[] = [];
  addresses: Address[] = [];
  vehicles: Vehicle[] = [];
  dependents: Dependent[] = [];
  isLoading = true;
  errorMessage = '';
  
  // Datos de pólizas
  polizas: any[] = [];
  selectedPoliza: any = null;
  polizaProducts: any[] = [];
  
  // Cache de productos
  private cachedProducts: Product[] | null = null;
  
  // Datos de cobertura
  currentAccount: string = '';
  currentPolizaId: string = '';

  constructor(
    private loaderService: LoaderService,
    private notificationService: NotificationService,
     private dialog: MatDialog,
  ){}

  ngOnInit() {
    this.loadCurrentAccount();
    this.loadUserProfile();
  }



  private loadCurrentAccount() {
    this.currentAccount = localStorage.getItem('account') || '';
    this.currentPolizaId = localStorage.getItem('currentPolizaId') || '';
    
    // Para la carga inicial, usar datos del usuario y producto inicial
    if (!this.currentPolizaId) {
      const userStr = localStorage.getItem('user');
      if (userStr) {
        try {
          const user = JSON.parse(userStr);
          if (user.memberships) {
            // memberships es un string, no un array
            this.currentPolizaId = user.memberships;
            localStorage.setItem('currentPolizaId', this.currentPolizaId);
          }
        } catch (e) {
          console.warn('Error parsing user from localStorage');
        }
      }
    }
  }

  isProductSelected(product: any): boolean {
    const matchesAccount = product.product === this.currentAccount;
    const matchesPoliza = this.currentPolizaId === this.selectedPoliza?.insurance;
    const isInCurrentPoliza = this.polizaProducts.some((p: any) => 
      p.id === product.id && p.product === product.product
    );
    
    return matchesAccount && matchesPoliza && isInCurrentPoliza;
  }

  onSelectProduct(product: any) {
    if (!product.active) {
      this.notificationService.showError('No puedes seleccionar un producto inactivo');
      return;
    }

    // Verificar si ya está seleccionado usando la nueva lógica
    const isAlreadySelected = product.product === this.currentAccount && 
                             this.currentPolizaId === this.selectedPoliza?.insurance;
    
    if (isAlreadySelected) {
      return; // Ya está seleccionado
    }

    this.notificationService.showConfirm(`¿Deseas cambiar tu cobertura a "${product.name}"?`)
      .subscribe(result => {
        if (result) {
          this.changeCoverage(product);
        }
      });
  }

  private changeCoverage(product: any) {
    const res = localStorage.getItem('user');
    const cognito = res ? JSON.parse(res).cognito : null;
    
    if (!cognito || !this.selectedPoliza) {
      this.notificationService.showError('Error: No se pudo obtener la información necesaria');
      return;
    }

    this.loaderService.show();
    
    console.log('Enviando changeCoverage:', {
      cognito,
      idProduct: product.id,
      idAssurance: this.selectedPoliza.insurance
    });
    
    this.userService.changeCoverage(cognito, product.id, this.selectedPoliza.insurance)
      .subscribe({
        next: (response) => {
          if (response.success || response.code === 200) {
            localStorage.setItem('account', product.product);
            localStorage.setItem('ProductByAccount', JSON.stringify(product));
            localStorage.setItem('currentPolizaId', this.selectedPoliza.insurance);
            
            // Actualizar user.memberships en localStorage
            const userStr = localStorage.getItem('user');
            if (userStr) {
              try {
                const user = JSON.parse(userStr);
                user.memberships = this.selectedPoliza.insurance;
                localStorage.setItem('user', JSON.stringify(user));
              } catch (e) {
                console.warn('Error updating user memberships');
              }
            }
            
            this.currentAccount = product.product;
            this.currentPolizaId = this.selectedPoliza.insurance;
            
            // Actualizar selectedPoliza en localStorage cuando cambia el producto
            if (this.selectedPoliza) {
              localStorage.setItem('selectedPoliza', JSON.stringify(this.selectedPoliza));
            }
            
            this.notificationService.showSuccess(`Cobertura cambiada a "${product.name}"`);
            
            
          } else {
            this.notificationService.showError('Error al cambiar la cobertura');
          }
          this.loaderService.hide();
        },
        error: (error) => {
          console.error('Error changing coverage:', error);
          this.notificationService.showError('Error al cambiar la cobertura');
          this.loaderService.hide();
        }
      });
  }

  private loadUserProfile() {
    setTimeout(() => {
      this.loaderService.show();
      
      const res = localStorage.getItem('user');
      const cognito = res ? JSON.parse(res).cognito : null;

      this.userService.getUserProfile(cognito).subscribe({
        next: (response: UserProfileResponse) => {
          if (response.success || response.code === 200) {
            
            this.addresses = response.address || [];
            this.vehicles = response.vehicles || [];
            this.pets = response.pets || [];
            this.dependents = response.dependents || [];
        
            
            // Cargar pólizas para clientes Banamex
            if (this.isAccountBanamex()) {
              this.loadPolizas();
            }
            
            localStorage.setItem('profileData', JSON.stringify(response));
          } else {
            this.notificationService.showError('Error al cargar los datos del perfil');
          }
          this.isLoading = false;
          this.loaderService.hide();
        },
        error: (error) => {
          this.notificationService.showError(`Error al conectar con el servidor (${error.status})`);
          this.isLoading = false;
          this.loaderService.hide();
        }
      });
    });
  }

  navigateToProfile(section: string) {
    this.router.navigate([`/profile-${section}`]);
  }

  // Helper methods para mostrar información resumida en las tarjetas
  getVehicleDisplayText(vehicle: Vehicle): string {
    return `${vehicle.catalogbrand?.description || 'Marca desconocida'} ${vehicle.catalogsubbrand?.description || vehicle.model || ''}, ${vehicle.plates}`;
  }

  getPetDisplayText(pet: Pet): string {
    return `${pet.specie}, ${pet.age} años`;
  }

  getAddressDisplayText(address: Address): string {
    return `${address.streetNumber || ''} ${address.colony}`.trim();
  }

  getDependentDisplayText(dependent: Dependent): string {
    return dependent.catalog?.description || 'Relación no especificada';
  }

  formatDateRange(startDate: string, endDate: string): string {
    if (!startDate || !endDate) return 'Fechas no disponibles';
    
    const formatDate = (dateStr: string): string => {
      const date = new Date(dateStr);
      return date.toLocaleDateString('es-ES', {
        day: '2-digit',
        month: '2-digit', 
        year: 'numeric'
      });
    };
    
    return `${formatDate(startDate)} - ${formatDate(endDate)}`;
  }

  private formatDateFromISO(dateStr: string): string {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toISOString().slice(0, 19).replace('T', ' ');
  }


  getAccountId(): string | null {
    return localStorage.getItem('account');
  }

  onEdit(item: any) {
    if (!item) return;

    // Dirección
    if ('idAddress' in item) {
      sessionStorage.setItem('direccionParaEditar', JSON.stringify(item));
      this.router.navigate(['/update-direccion']);
    }
    // Dependiente
    else if ('dependent' in item && 'catalog' in item) {
      sessionStorage.setItem('dependientesParaEditar', JSON.stringify(item));
      this.router.navigate(['/update-dependientes']);
    }
    // Vehículo
    else if ('plates' in item && 'serialNumber' in item) {
      sessionStorage.setItem('vehicleParaEditar', JSON.stringify(item));
      this.router.navigate(['/update-register']);
    }
    // Mascota
    else if ('idPet' in item && 'petName' in item) {
      sessionStorage.setItem('petParaEditar', JSON.stringify(item));
      this.router.navigate(['/update-pet']);
    }
    else {
      console.warn('Tipo de item no reconocido para edición:', item);
    }
  }

  isAccountBanamex(): boolean {
    let allowedAccounts = ['2228', '2665', '2559', '2907', '2988', '2997', '2998', '2999'];
    const account = localStorage.getItem('account');
    return account ? allowedAccounts.includes(account) : false;
  }

  // Método público para recargar pólizas desde otros componentes
  reloadPolizas(): void {
    if (this.isAccountBanamex()) {
      this.loadPolizas();
    }
  }

  private loadPolizas() {
    const res = localStorage.getItem('user');
    const cognito = res ? JSON.parse(res).cognito : null;
    const currentSelectedInsurance = this.selectedPoliza?.insurance;
    
    this.userService.getPolizas(cognito).subscribe({
      next: (response: any) => {
        if (response.code === 200 && response.data) {
          // Consolidar pólizas duplicadas
          const polizasMap = new Map();
          
          response.data.forEach((item: any) => {
            const products = Array.isArray(item.products) 
              ? item.products.filter((p: any) => p !== null && p.registerdate !== null) 
              : (item.products && item.products.registerdate !== null ? [item.products] : []);
            
            if (polizasMap.has(item.insurance)) {
              // Si ya existe, combinar productos
              const existing = polizasMap.get(item.insurance);
              existing.products = [...existing.products, ...products];
              // Usar las fechas más completas (las que no sean null)
              if (item.start_date && !existing.start_date) existing.start_date = item.start_date;
              if (item.end_date && !existing.end_date) existing.end_date = item.end_date;
            } else {
              // Nueva póliza
              polizasMap.set(item.insurance, {
                insurance: item.insurance,
                start_date: item.start_date,
                end_date: item.end_date,
                products: products
              });
            }
          });
          
          this.polizas = Array.from(polizasMap.values()).filter(poliza => poliza.products.length > 0);
          
          // Seleccionar la póliza inicial basada en currentPolizaId o savedPoliza
          const savedPoliza = localStorage.getItem('selectedPoliza');
          
          if (this.currentPolizaId) {
            // Usar currentPolizaId (desde memberships o previamente guardado)
            this.selectedPoliza = this.polizas.find(p => p.insurance === this.currentPolizaId) || this.polizas[0];
          } else if (savedPoliza) {
            // Usar póliza guardada previamente
            try {
              const parsedPoliza = JSON.parse(savedPoliza);
              this.selectedPoliza = this.polizas.find(p => p.insurance === parsedPoliza.insurance) || this.polizas[0];
            } catch (e) {
              this.selectedPoliza = this.polizas[0];
            }
          } else {
            // Usar la primera póliza disponible
            this.selectedPoliza = this.polizas[0];
          }
          
          // Actualizar localStorage con la póliza seleccionada
          if (this.selectedPoliza) {
            localStorage.setItem('selectedPoliza', JSON.stringify(this.selectedPoliza));
          }
          
          this.loadPolizaProducts();
        }
      },
      error: (error) => {
        console.error('Error loading polizas:', error);
      }
    });
  }

  private loadPolizaProducts() {
    if (this.selectedPoliza && this.selectedPoliza.products) {
      this.polizaProducts = this.selectedPoliza.products;
    } else {
      this.polizaProducts = [];
    }
  }

  onPolizaChange() {
    if (this.selectedPoliza) {
      localStorage.setItem('selectedPoliza', JSON.stringify(this.selectedPoliza));
      
      // Verificar si el producto actual pertenece a esta póliza
      const currentProductInThisPoliza = this.selectedPoliza.products?.find(
        (p: any) => p.product === this.currentAccount
      );
    }
    this.loadPolizaProducts();
  }

  onDeleteProduct(product: any) {
    this.notificationService.showConfirm('¿Está seguro que desea eliminar este producto?')
      .subscribe(result => {
        if (result) {
          this.deleteProductFromPoliza(product);
        }
      });
  }

  private deleteProductFromPoliza(product: any) {
    const res = localStorage.getItem('user');
    const cognito = res ? JSON.parse(res).cognito : null;
    
    if (!cognito || !this.selectedPoliza) {
      this.notificationService.showError('Error: No se pudo obtener la información necesaria');
      return;
    }

    this.loaderService.show();
    console.log(this.selectedPoliza);
    
    this.userService.deleteProductFromPoliza(cognito, this.selectedPoliza.insurance.toString(), product.product.toString())
      .subscribe({
        next: (response) => {
          if (response.success || response.code === 200) {
            const index = this.polizaProducts.indexOf(product);
            if (index > -1) {
              this.polizaProducts.splice(index, 1);
            }
            this.notificationService.showSuccess('Producto eliminado correctamente');
          } else {
            this.notificationService.showError('Error al eliminar el producto');
          }
          this.loaderService.hide();
        },
        error: (error) => {
          console.error('Error deleting product:', error);
          this.notificationService.showError('Error al eliminar el producto');
          this.loaderService.hide();
        }
      });
  }

  onAddNewPoliza() {
    if (this.cachedProducts) {
      const filteredProducts = this.cachedProducts.filter(product => product.registerdate !== null);
      this.openProductModal(filteredProducts, false);
    } else {
      this.loadProductsAndOpenModal(false);
    }
  }

  onCreateNewPoliza() {
    if (this.cachedProducts) {
      const filteredProducts = this.cachedProducts.filter(product => product.registerdate !== null);
      this.openProductModal(filteredProducts, true);
    } else {
      this.loadProductsAndOpenModal(true);
    }
  }

  private loadProductsAndOpenModal(isNewPoliza: boolean = false) {
    this.loaderService.show();
    
    this.userService.getProducts().subscribe({
      next: (response) => {
        this.loaderService.hide();
        
        if (response.success) {
          this.cachedProducts = response.data.filter(product => product.active && product.registerdate !== null);
          this.openProductModal(this.cachedProducts, isNewPoliza);
        } else {
          this.notificationService.showError('Error al cargar los productos');
        }
      },
      error: (error) => {
        this.loaderService.hide();
        console.error('Error loading products:', error);
        this.notificationService.showError('Error al cargar los productos');
      }
    });
  }

  private openProductModal(products: Product[], isNewPoliza: boolean = false) {
    const dialogRef = this.dialog.open(AddPolizaModalComponent, {
      width: '500px',
      disableClose: true,
      hasBackdrop: true,
      backdropClass: 'modal-backdrop',
      data: { products, isNewPoliza }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        if (isNewPoliza) {
          this.createNewPoliza(result);
        } else {
          this.addProductToPoliza(result);
        }
      }
    });
  }

  private addProductToPoliza(product: any) {
    const res = localStorage.getItem('user');
    const cognito = res ? JSON.parse(res).cognito : null;
    
    if (!cognito || !this.selectedPoliza) {
      this.notificationService.showError('Error: No se pudo obtener la información necesaria');
      return;
    }

    this.loaderService.show();
    
    this.userService.addProductToPoliza(cognito, this.selectedPoliza.insurance, product.product, this.formatDateFromISO(this.selectedPoliza.start_date), this.formatDateFromISO(this.selectedPoliza.end_date))
      .subscribe({
        next: (response) => {
          if (response.success || response.code === 200) {
            this.notificationService.showSuccess(`Producto "${product.name}" agregado correctamente`);
            if (product.reloadList) {
              this.loadPolizas();
            }
          } else {
            this.notificationService.showError('Error al agregar el producto');
          }
          this.loaderService.hide();
        },
        error: (error) => {
          console.error('Error adding product:', error);
          this.notificationService.showError('Error al agregar el producto');
          this.loaderService.hide();
        }
      });
  }

  private createNewPoliza(polizaData: any) {
    const res = localStorage.getItem('user');
    const cognito = res ? JSON.parse(res).cognito : null;
    
    if (!cognito) {
      this.notificationService.showError('Error: No se pudo obtener la información del usuario');
      return;
    }

    this.loaderService.show();
    
    this.userService.addProductToPoliza(cognito, polizaData.polizaNumber, polizaData.product.product, polizaData.startDate, polizaData.endDate)
      .subscribe({
        next: (response) => {
          if (response.success || response.code === 200) {
            this.notificationService.showSuccess(`Póliza "${polizaData.polizaNumber}" creada correctamente`);
            this.loadPolizas(); // Recargar las pólizas
          } else {
            this.notificationService.showError('Error al crear la póliza');
          }
          this.loaderService.hide();
        },
        error: (error) => {
          console.error('Error creating poliza:', error);
          this.notificationService.showError('Error al crear la póliza');
          this.loaderService.hide();
        }
      });
  }


}
